import React, { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import Select from "react-select";
import * as Yup from "yup";
import { buildAbsoluteDownloadUrl } from "../../../utils/FileDownload";
import {
    DEFAULT_CLOSE_TIME,
    DEFAULT_OPEN_TIME,
    OPERATING_HOUR_DAYS,
    getDefaultOperatingHours,
    mapBuildingsToSelect,
    updateOperatingHoursByDay,
    updateOperatingHoursTimeField
} from "../../../utils/CampusBuildingsUtil";
import { tokens } from "../../../public/tokens";
import Button from "../../common/Button/Button";
import StandardModal from "../../common/Modal/StandardModal";
import FormLabel from "../../common/forms/FormLabel";
import FormInput from "../../common/forms/FormInput";
import { selectStyles as sharedSelectStyles } from "../../common/forms/selectStyles";

const { colors, typography, effects, globals } = tokens;
const { neutral, states, text: textColors, ui, surfaces } = colors;

const colourPickerContainerStyle = {
    height: "42px",
    borderRadius: globals["border-radius"],
    border: `1px solid ${ui.border}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingLeft: "10px",
    paddingRight: "5px",
    overflow: "hidden"
};

const colourTextStyle = {
    border: "none",
    outline: "none",
    flex: 1,
    fontSize: typography.fontSize["lg-14px"],
    fontFamily: globals["font-family"],
    color: textColors.primary
};

const operatingHoursContainerStyle = {
    border: `1px solid ${ui.border}`,
    borderRadius: globals["border-radius"],
    boxShadow: effects.effects.shadow.default,
    backgroundColor: neutral.white,
    padding: "20px"
};

const AddEditBuildingModal = ({
    show,
    onHide,
    building,
    campusId,
    onSave,
    saving,
    netsuiteBuildingList
}) => {
    const isEditMode = !!building?.id;
    const [inheritFromCampus, setInheritFromCampus] = useState(building?.inherit_from_campus || false);

    useEffect(() => {
        setInheritFromCampus(building?.inherit_from_campus || false);
    }, [building]);

    const existingImageFile = useMemo(() => {
        if (show && building?.mapImage) {
            return building.mapImage;
        }
        return null;
    }, [show, building?.mapImage]);

    const netsuiteBuildings = useMemo(
        () => mapBuildingsToSelect(netsuiteBuildingList),
        [netsuiteBuildingList]
    );

    const initialValues = useMemo(() => ({
        name: building?.name || "",
        open_time: building?.open_time || DEFAULT_OPEN_TIME,
        close_time: building?.close_time || DEFAULT_CLOSE_TIME,
        grouping_color: building?.grouping_color || "#E3B81C",
        operating_hours: building?.operatingHoursObj || getDefaultOperatingHours(),
        map_image: null,
        map_image_url: building?.mapImage || null,
        netsuite_id: Number(building?.netsuite_id ?? 0)
    }), [building]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues,
        validateOnChange: true,
        validateOnBlur: true,
        validationSchema: Yup.object({
            name: Yup.string().required("Building name is required"),
            open_time: Yup.string().required("Open time is required"),
            close_time: Yup.string().required("Close time is required"),
            grouping_color: Yup.string().required("Grouping color is required"),
            netsuite_id: Yup.number()
                .moreThan(0, "Netsuite Building is required")
                .required("Netsuite Building is required")
        }),
        onSubmit: async (values, { resetForm }) => {
            const buildingData = {
                id: building?.id,
                name: values.name,
                open_time: values.open_time,
                close_time: values.close_time,
                grouping_color: values.grouping_color,
                operating_hours: values.operating_hours,
                campus_id: campusId,
                netsuite_id: values.netsuite_id
            };

            if (values.map_image && values.map_image instanceof File) {
                buildingData.map_image = values.map_image;
            } else if (values.map_image_url && !values.map_image) {
                buildingData.map_image = values.map_image_url;
            }

            await onSave(buildingData);

            resetForm();
        }
    });

    const handleOperatingDayToggle = (day, enabled) => {
        if (inheritFromCampus) setInheritFromCampus(false);

        const currentDay = formik.values.operating_hours[day];
        const currentRange = currentDay?.ranges?.[0] || {
            start: DEFAULT_OPEN_TIME,
            end: DEFAULT_CLOSE_TIME
        };

        const updated = updateOperatingHoursByDay({
            operatingHours: formik.values.operating_hours,
            day,
            enabled,
            timeRange: `${currentRange.start}-${currentRange.end}`
        });

        formik.setFieldValue("operating_hours", updated);
    };

    const handleOperatingTimeChange = (day, field, value) => {
        if (inheritFromCampus) setInheritFromCampus(false);

        const updated = updateOperatingHoursTimeField({
            operatingHours: formik.values.operating_hours,
            day,
            field,
            value
        });

        formik.setFieldValue("operating_hours", updated);
    };

    const handleMapImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            formik.setFieldValue("map_image", file);
            formik.setFieldValue("map_image_url", null);
        }
    };

    const currentCampusSelected = netsuiteBuildings.find(
        (opt) => opt.value === formik.values.netsuite_id
    );

    return (
        <StandardModal
            show={show}
            onHide={onHide}
            title={isEditMode ? "Edit Campus Building" : "Add Campus Building"}
            icon={<i className="fal fa-university" />}
            size="lg"
            onSave={formik.handleSubmit}
            saveLoading={saving}
            saveDisabled={saving || !formik.isValid}
            cancelLabel="Cancel"
        >
            <form onSubmit={formik.handleSubmit}>
                {/* Name */}
                <div className="form-group mb-4">
                    <FormLabel required>Name</FormLabel>
                    <FormInput
                        {...formik.getFieldProps("name")}
                        error={formik.errors.name}
                        touched={formik.touched.name}
                        placeholder="Name"
                    />
                </div>

                <div className="row align-items-start">
                    {/* Campus Building (NetSuite) */}
                    <div className="col-md-4">
                        <div className="form-group mb-4">
                            <FormLabel required>Campus Building</FormLabel>
                            <Select
                                options={netsuiteBuildings}
                                value={currentCampusSelected}
                                onChange={(opt) => formik.setFieldValue("netsuite_id", opt?.value ?? 0)}
                                styles={sharedSelectStyles}
                                menuPortalTarget={document.body}
                            />
                            {formik.errors.netsuite_id && formik.touched.netsuite_id && (
                                <div className="invalid-feedback d-block">{formik.errors.netsuite_id}</div>
                            )}
                        </div>
                    </div>

                    {/* Grouping Colour */}
                    <div className="col-md-4">
                        <div className="form-group mb-4">
                            <FormLabel required>Grouping Colour</FormLabel>
                            <div style={colourPickerContainerStyle}>
                                <input
                                    type="text"
                                    value={formik.values.grouping_color}
                                    onChange={(e) => formik.setFieldValue("grouping_color", e.target.value)}
                                    style={colourTextStyle}
                                />
                                <label
                                    className="colour-picker-swatch"
                                    style={{
                                        backgroundColor: formik.values.grouping_color,
                                        width: "30px",
                                        height: "30px",
                                        border: `1px solid ${ui.border}`,
                                        borderRadius: globals["border-radius"],
                                        boxShadow: effects.effects.shadow.default,
                                        cursor: "pointer",
                                        overflow: "hidden",
                                        display: "block",
                                        flexShrink: 0,
                                        position: "relative",
                                        margin: "auto 0"
                                    }}
                                >
                                    <input
                                        className="colour-picker-input"
                                        type="color"
                                        value={formik.values.grouping_color}
                                        onChange={(e) => formik.setFieldValue("grouping_color", e.target.value)}
                                        style={{
                                            position: "absolute",
                                            width: "200%",
                                            height: "200%",
                                            top: "-50%",
                                            left: "-50%",
                                            opacity: 0,
                                            cursor: "pointer",
                                            border: "none",
                                            padding: 0
                                        }}
                                    />
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Map Image */}
                    <div className="col-md-4">
                        <div className="form-group mb-4">
                            <FormLabel>Map Image</FormLabel>
                            <input
                                id="mapImageUpload"
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={handleMapImageUpload}
                            />
                            <div className="d-flex align-items-center" style={{ gap: globals["spacing-sm"] }}>
                                <Button
                                    variant="secondary"
                                    iconLeft={<i className="fal fa-upload" />}
                                    onClick={() => document.getElementById("mapImageUpload")?.click()}
                                >
                                    Upload
                                </Button>
                                <Button
                                    variant="primary"
                                    iconLeft={<i className="fal fa-cloud-download" />}
                                    onClick={() => document.getElementById("mapImageUpload")?.click()}
                                >
                                    Browse
                                </Button>
                            </div>
                            {(formik.values.map_image || formik.values.map_image_url) && (
                                <div style={{ marginTop: "8px", fontSize: typography.fontSize["sm-12px"], color: textColors.secondary }}>
                                    {formik.values.map_image
                                        ? formik.values.map_image.name
                                        : formik.values.map_image_url?.split("/").pop() || "Current image"}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Operating Hours */}
                <div className="form-group mb-3">
                    <FormLabel required>Operating Hours</FormLabel>
                    <div style={operatingHoursContainerStyle}>
                        {OPERATING_HOUR_DAYS.map((day, index) => {
                            const dayState = formik.values.operating_hours[day.key];
                            const isEnabled = dayState?.enabled || false;
                            const firstRange = dayState?.ranges?.[0] || {
                                start: DEFAULT_OPEN_TIME,
                                end: DEFAULT_CLOSE_TIME
                            };

                            return (
                                <div
                                    key={day.key}
                                    className="d-flex align-items-center"
                                    style={{
                                        marginBottom: index === OPERATING_HOUR_DAYS.length - 1 ? 0 : "24px"
                                    }}
                                >
                                    <div style={{ width: "160px", display: "flex", alignItems: "center", gap: "10px" }}>
                                        <label className="switch mb-0">
                                            <input
                                                type="checkbox"
                                                checked={isEnabled}
                                                onChange={(e) => handleOperatingDayToggle(day.key, e.target.checked)}
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                        <span style={{ color: textColors.secondary, fontSize: typography.fontSize["lg-14px"], fontFamily: globals["font-family"] }}>
                                            {day.label}
                                        </span>
                                    </div>
                                    <div className="d-flex flex-grow-1 align-items-center">
                                        <FormInput
                                            type="time"
                                            value={firstRange.start}
                                            disabled={!isEnabled}
                                            onChange={(e) => handleOperatingTimeChange(day.key, "start", e.target.value)}
                                            style={{ marginRight: "12px" }}
                                        />
                                        <span className="mx-3" style={{ color: textColors.primary, fontSize: typography.fontSize["lg-14px"] }}>-</span>
                                        <FormInput
                                            type="time"
                                            value={firstRange.end}
                                            disabled={!isEnabled}
                                            onChange={(e) => handleOperatingTimeChange(day.key, "end", e.target.value)}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </form>
        </StandardModal>
    );
};

export default AddEditBuildingModal;
